import * as React from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';

import { DownloadIcon, FileIcon, MinusCircleIcon, PlusIcon, RefreshIcon } from '../../icons';

import { getDocumentCategory, DocumentCategory, type IViewerDocument } from './documentUtils';

interface IDocumentPreviewProps {
  document: IViewerDocument | null;
}

const DocumentPreview = ({ document }: IDocumentPreviewProps) => {
  const content = (() => {
    if (!document) {
      return (
        <div className="text-tertiary flex flex-1 items-center justify-center text-base">
          <p>Select a document to preview</p>
        </div>
      );
    }

    const category = getDocumentCategory(document.mimeType);

    switch (category) {
      case DocumentCategory.PDF:
        return <PdfPreview url={document.assetUrl} />;
      case DocumentCategory.EMAIL:
        return <EmailPreview url={document.assetUrl} emailMetadata={document.emailMetadata} />;
      case DocumentCategory.IMAGE:
        return <ImagePreview url={document.assetUrl} />;
      default:
        return <UnsupportedPreview document={document} />;
    }
  })();

  return (
    <div className="border-l-secondary border-t-secondary flex flex-1 flex-col overflow-hidden border-l border-t">
      {content}
    </div>
  );
};

const PdfPreview = ({ url }: { url: string }) => {
  const [status, setStatus] = React.useState<'loading' | 'ok' | 'failed'>('loading');
  const objectRef = React.useRef<HTMLObjectElement>(null);

  React.useEffect(() => {
    setStatus('loading');

    const el = objectRef.current;
    if (!el) {
      return;
    }

    const handleLoad = () => {
      try {
        const doc = el.contentDocument;
        if (doc && doc.body && doc.body.childElementCount === 0 && doc.body.innerText === '') {
          setStatus('failed');
        } else {
          setStatus('ok');
        }
      } catch {
        setStatus('ok');
      }
    };

    const handleError = () => setStatus('failed');

    el.addEventListener('load', handleLoad);
    el.addEventListener('error', handleError);

    const timeout = setTimeout(() => {
      setStatus((s) => (s === 'loading' ? 'failed' : s));
    }, 8000);

    return () => {
      el.removeEventListener('load', handleLoad);
      el.removeEventListener('error', handleError);
      clearTimeout(timeout);
    };
  }, [url]);

  if (status === 'failed') {
    return <UnsupportedPreview document={{ id: '', assetUrl: url }} />;
  }

  return <object ref={objectRef} data={url} type="application/pdf" className="flex-1" />;
};

interface IEmailPreviewProps {
  url: string;
  emailMetadata?: IViewerDocument['emailMetadata'];
}

const EmailPreview = ({ url, emailMetadata }: IEmailPreviewProps) => {
  const hasMetadata =
    emailMetadata && (emailMetadata.originalFrom || emailMetadata.originalTo || emailMetadata.originalSubject);

  return (
    <div className="bg-quaternary flex flex-1 flex-col overflow-hidden">
      {hasMetadata && (
        <div className="border-b-secondary flex flex-col gap-1 border-b bg-white px-6 py-4">
          {emailMetadata.originalSubject && (
            <div className="text-primary text-base font-semibold">{emailMetadata.originalSubject}</div>
          )}
          <div className="flex flex-col gap-0.5">
            {emailMetadata.originalFrom && (
              <div className="text-secondary text-sm">
                <span className="text-tertiary font-medium">From: </span>
                {emailMetadata.originalFrom}
              </div>
            )}
            {emailMetadata.originalTo && (
              <div className="text-secondary text-sm">
                <span className="text-tertiary font-medium">To: </span>
                {emailMetadata.originalTo}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="bg-quaternary flex flex-1 overflow-hidden p-6">
        <iframe
          key={url}
          src={url}
          title="Email preview"
          sandbox="allow-same-origin"
          className="flex-1 rounded-lg border-none bg-white shadow-lg"
        />
      </div>
    </div>
  );
};

const ImagePreview = ({ url }: { url: string }) => (
  <TransformWrapper
    key={url}
    initialScale={1}
    minScale={0.5}
    maxScale={5}
    centerOnInit
    doubleClick={{ mode: 'reset' }}
    wheel={{ step: 0.15, smoothStep: 0.02 }}
  >
    <div className="bg-quaternary relative flex h-full w-full flex-1 overflow-hidden">
      <ZoomControls />
      <TransformComponent
        wrapperStyle={{ width: '100%', height: '100%' }}
        contentStyle={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={url} alt="Document preview" className="max-h-full max-w-full rounded-lg object-contain shadow-lg" />
      </TransformComponent>
    </div>
  </TransformWrapper>
);

const ZoomControls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-lg bg-white/90 p-1 shadow-md backdrop-blur-sm">
      <ZoomButton onClick={() => zoomIn()} label="Zoom in">
        <PlusIcon className="size-4" />
      </ZoomButton>
      <ZoomButton onClick={() => zoomOut()} label="Zoom out">
        <MinusCircleIcon className="size-4" />
      </ZoomButton>
      <div className="bg-secondary mx-0.5 h-4 w-px" />
      <ZoomButton onClick={() => resetTransform()} label="Reset zoom">
        <RefreshIcon className="size-4" />
      </ZoomButton>
    </div>
  );
};

const ZoomButton = ({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="text-secondary hover:text-primary hover:bg-tertiary rounded-md p-1.5 transition-colors"
  >
    {children}
  </button>
);

const UnsupportedPreview = ({ document }: { document: IViewerDocument }) => (
  <div className="bg-quaternary text-tertiary flex flex-1 flex-col items-center justify-center gap-4 text-base">
    <FileIcon className="size-12" />
    <p>Preview not available for this file type</p>
    <a
      href={document.assetUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-utility-brand-600 hover:text-utility-brand-700 flex items-center gap-1.5 font-medium"
    >
      <DownloadIcon className="size-4 shrink-0" />
      Download
    </a>
  </div>
);

export default DocumentPreview;
